/**
 * =============================================================================
 * IDS CRM Web - Signature pad (Workscope forms Phase 1, Gareth 2026-10-22)
 * =============================================================================
 *
 * A signature drawn with a finger, stylus or mouse, kept as a PNG data URL:
 * the shape the form PDF already prints (checkSubmissionPdf). Clear starts
 * again. Read only (or disabled) it shows the signature as an image.
 * =============================================================================
 */

import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './SignaturePad.css';

const HEIGHT = 160;

function SignaturePad({ value, onChange, disabled = false, label = 'Signature' }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const drewRef = useRef(false);

  // Size the canvas to its box (sharp on high-density screens) and put back
  // any signature already given.
  const prepare = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 400;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(HEIGHT * ratio);
    const context = canvas.getContext && canvas.getContext('2d');
    if (!context) return;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#111827';
    if (value && typeof value === 'string' && value.startsWith('data:image')) {
      const image = new Image();
      image.onload = () => context.drawImage(image, 0, 0, width, HEIGHT);
      image.src = value;
    }
  // Only on first draw: redrawing on every value change would fight the pen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!disabled) prepare();
  }, [disabled, prepare]);

  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event) => {
    if (disabled) return;
    const context = canvasRef.current?.getContext?.('2d');
    if (!context) return;
    event.preventDefault();
    canvasRef.current.setPointerCapture?.(event.pointerId);
    drawingRef.current = true;
    const { x, y } = point(event);
    context.beginPath();
    context.moveTo(x, y);
  };

  const move = (event) => {
    if (!drawingRef.current) return;
    const context = canvasRef.current?.getContext?.('2d');
    if (!context) return;
    event.preventDefault();
    const { x, y } = point(event);
    context.lineTo(x, y);
    context.stroke();
    drewRef.current = true;
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (drewRef.current && canvasRef.current?.toDataURL) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext?.('2d');
    if (context) context.clearRect(0, 0, canvas.width, canvas.height);
    drewRef.current = false;
    onChange('');
  };

  if (disabled) {
    return value && String(value).startsWith('data:image')
      ? <img className="signature-pad-image" src={value} alt={label} />
      : <div className="signature-pad-empty">Not signed</div>;
  }

  return (
    <div className="signature-pad">
      <canvas
        ref={canvasRef}
        className="signature-pad-canvas"
        style={{ height: `${HEIGHT}px` }}
        aria-label={`${label}: sign here`}
        role="img"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
      />
      <div className="signature-pad-actions">
        <span className="signature-pad-hint">{value ? 'Signed' : 'Sign in the box'}</span>
        <button type="button" className="signature-pad-clear" onClick={clear}>Clear</button>
      </div>
    </div>
  );
}

SignaturePad.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string
};

export default SignaturePad;
